import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Plus, ChevronDown, ChevronRight, Search, Trash2 } from 'lucide-react';
import { formatDate } from '../../utils/dateFormatter';
import { canWrite } from '../../utils/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const [client, setClient] = useState(null);
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    notes: '',
    payment_method: 'cash'
  });

  const fetchClientDetails = async () => {
    try {
      const response = await axios.get(`${API}/clients/by-id/${id}`, { headers: getAuthHeader() });
      setClient(response.data.client);
      setOrders(response.data.orders);
      setPayments(response.data.payments);

      // Fetch invoices for this client
      const invoicesResponse = await axios.get(`${API}/invoices`, { headers: getAuthHeader() });
      const clientInvoices = invoicesResponse.data.filter(inv => inv.client_name === response.data.client.name);
      setInvoices(clientInvoices);
    } catch (error) {
      toast.error('Failed to fetch client details');
      navigate('/finance');
    }
  };

  useEffect(() => {
    fetchClientDetails();
  }, [id]);

  const handleDeletePayment = async (paymentId) => {
    try {
      await axios.delete(`${API}/payments/delete/${paymentId}`, { headers: getAuthHeader() });
      toast.success('Payment deleted');
      fetchClientDetails();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/payments`, {
        entity_id: client.name,
        entity_type: 'client',
        ...paymentData
      }, { headers: getAuthHeader() });
      toast.success('Payment recorded');
      setIsPaymentOpen(false);
      fetchClientDetails();
      setPaymentData({ amount: 0, date: new Date().toISOString().split('T')[0], notes: '', payment_method: 'cash' });
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: 'bg-warning/20 text-warning',
      fulfilled: 'bg-success/20 text-success',
      cancelled: 'bg-destructive/20 text-destructive'
    };
    return statusColors[status] || statusColors.pending;
  };

  const availableYears = [...new Set([
    ...orders.map(o => (o.created_at || '').slice(0, 4)),
    ...invoices.map(i => (i.date || '').slice(0, 4)),
    ...payments.map(p => (p.date || '').slice(0, 4)),
  ].filter(Boolean))].sort().reverse();

  const filteredOrders = orders.filter(o => {
    const matchesStatus = statusFilter === 'all' || o.status === statusFilter;
    const matchesYear = yearFilter === 'all' || (o.created_at || '').slice(0, 4) === yearFilter;
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q ||
      o.order_number?.toLowerCase().includes(q) ||
      o.items?.toLowerCase().includes(q);
    return matchesStatus && matchesYear && matchesSearch;
  });

  const filteredInvoices = invoices.filter(i =>
    yearFilter === 'all' || (i.date || '').slice(0, 4) === yearFilter
  );

  const filteredPayments = payments.filter(p =>
    yearFilter === 'all' || (p.date || '').slice(0, 4) === yearFilter
  );

  if (!client) return <div className="p-8">Loading...</div>;

  const totalInvoices = invoices.reduce((sum, inv) => sum + inv.total, 0);
  const totalPayments = payments.reduce((sum, p) => sum + p.amount, 0);
  const runningBalance = totalInvoices - totalPayments;

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/finance')} data-testid="back-to-finance-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-4xl font-bold tracking-tight" lang={language === 'ar' ? 'ar' : 'en'} data-testid="client-detail-title">
          {client.name}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 border border-border">
          <h3 className="text-lg font-semibold mb-4" lang={language === 'ar' ? 'ar' : 'en'}>Contact Information</h3>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-muted-foreground">{t('email')}:</span>
              <p className="font-medium">{client.email || 'N/A'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">{t('phone')}:</span>
              <p className="font-medium">{client.phone || 'N/A'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">{t('address')}:</span>
              <p className="font-medium">{client.address || 'N/A'}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 border border-border">
          <h3 className="text-lg font-semibold mb-4" lang={language === 'ar' ? 'ar' : 'en'}>{t('orders')}</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('total')}:</span>
              <span className="font-mono-numeric font-semibold">{orders.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('completed')}:</span>
              <span className="font-mono-numeric font-semibold text-success">{orders.filter(o => o.status === 'fulfilled').length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('pending')}:</span>
              <span className="font-mono-numeric font-semibold text-warning">{orders.filter(o => o.status === 'pending').length}</span>
            </div>
          </div>
        </Card>

        <Card className="p-6 border border-border">
          <h3 className="text-lg font-semibold mb-4" lang={language === 'ar' ? 'ar' : 'en'}>{t('financialSummary')}</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('totalInvoiced')}:</span>
              <span className="font-mono-numeric font-semibold">{totalInvoices.toFixed(2)} {t('currency')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('totalPaid')}:</span>
              <span className="font-mono-numeric font-semibold text-success">{totalPayments.toFixed(2)} {t('currency')}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-border">
              <span className="text-muted-foreground font-semibold">{t('balance')}:</span>
              <span className={`font-mono-numeric font-bold text-lg ${runningBalance > 0 ? 'text-warning' : 'text-success'}`}>
                {runningBalance.toFixed(2)} {t('currency')}
              </span>
            </div>
          </div>
        </Card>
      </div>

      <Tabs defaultValue="orders" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="orders" data-testid="tab-client-orders">
            <span lang={language === 'ar' ? 'ar' : 'en'}>{t('orders')}</span>
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-client-payments">
            <span lang={language === 'ar' ? 'ar' : 'en'}>{t('paymentsAndInvoices')}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="orders">
          <Card className="p-6 border border-border">
            <h2 className="text-2xl font-semibold mb-4" lang={language === 'ar' ? 'ar' : 'en'}>{t('orders')}</h2>

            {/* Search + filter */}
            <div className="flex gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by order # or item..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <select
                value={yearFilter}
                onChange={e => setYearFilter(e.target.value)}
                className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="all">All Years</option>
                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="all">All ({orders.length})</option>
                <option value="pending">Pending ({orders.filter(o => o.status === 'pending').length})</option>
                <option value="fulfilled">Fulfilled ({orders.filter(o => o.status === 'fulfilled').length})</option>
                <option value="cancelled">Cancelled ({orders.filter(o => o.status === 'cancelled').length})</option>
              </select>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-muted/50 text-left">
                    <th className="p-3 w-8"></th>
                    <th className="p-3">Order #</th>
                    <th className="p-3">Card #</th>
                    <th className="p-3">Items</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Delivery</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order, idx) => (
                    <React.Fragment key={order.order_number}>
                      <tr
                        className="border-t border-border hover:bg-muted/30 cursor-pointer transition-colors"
                        onClick={() => setExpandedRow(expandedRow === order.order_number ? null : order.order_number)}
                        data-testid={`client-order-${idx}`}
                      >
                        <td className="p-3 text-muted-foreground">
                          {expandedRow === order.order_number
                            ? <ChevronDown className="w-4 h-4" />
                            : <ChevronRight className="w-4 h-4" />}
                        </td>
                        <td className="p-3 font-medium">#{order.order_number}</td>
                        <td className="p-3 text-muted-foreground">{order.card_number || '—'}</td>
                        <td className="p-3">
                          {order.items}
                          {order.item_code && <span className="text-xs text-muted-foreground ml-1">({order.item_code})</span>}
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs ${getStatusBadge(order.status)}`} lang={language === 'ar' ? 'ar' : 'en'}>
                            {t(order.status)}
                          </span>
                        </td>
                        <td className="p-3 text-muted-foreground whitespace-nowrap">
                          {order.delivery_date ? formatDate(order.delivery_date) : '—'}
                        </td>
                      </tr>

                      {/* Expanded detail row */}
                      {expandedRow === order.order_number && (
                        <tr className="border-t border-border bg-muted/20">
                          <td colSpan={6} className="p-4">
                            <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                              <div><span className="font-medium">Procurement:</span> {order.procurement_date ? formatDate(order.procurement_date) : '—'}</div>
                              <div><span className="font-medium">Production:</span> {order.production_date ? formatDate(order.production_date) : '—'}</div>
                              <div><span className="font-medium">Notes:</span> {order.notes || '—'}</div>
                            </div>
                            <table className="text-sm border-collapse">
                              <thead>
                                <tr className="bg-muted/50">
                                  <th className="border border-border px-3 py-1 text-left">Size</th>
                                  <th className="border border-border px-3 py-1 text-left">Color</th>
                                  <th className="border border-border px-3 py-1 text-right">Qty</th>
                                </tr>
                              </thead>
                              <tbody>
                                {order.variants?.map((v, vidx) => (
                                  <tr key={vidx}>
                                    <td className="border border-border px-3 py-1">{v.size}</td>
                                    <td className="border border-border px-3 py-1">{v.color}</td>
                                    <td className="border border-border px-3 py-1 text-right">{v.quantity}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredOrders.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <p lang={language === 'ar' ? 'ar' : 'en'}>
                  {searchQuery || statusFilter !== 'all' ? 'No orders match your filter.' : t('noOrders')}
                </p>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="payments">
          <Card className="p-6 border border-border">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('paymentsAndInvoices')}</h2>
              {canWrite(user?.role) && <Dialog open={isPaymentOpen} onOpenChange={setIsPaymentOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2 bg-accent hover:bg-accent/90" data-testid="add-payment-btn">
                    <Plus className="w-4 h-4" />
                    <span lang={language === 'ar' ? 'ar' : 'en'}>{t('recordPayment')}</span>
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>{t('recordPayment')}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddPayment} className="space-y-4">
                    <div>
                      <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('amount')} ({t('currency')})</Label>
                      <Input type="number" step="0.01" value={paymentData.amount} onChange={(e) => setPaymentData({...paymentData, amount: parseFloat(e.target.value)})} required data-testid="payment-amount-input" />
                    </div>
                    <div>
                      <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('date')}</Label>
                      <Input type="date" value={paymentData.date} onChange={(e) => setPaymentData({...paymentData, date: e.target.value})} required data-testid="payment-date-input" />
                    </div>
                    <div>
                      <Label lang={language === 'ar' ? 'ar' : 'en'}>Payment Method</Label>
                      <div className="flex gap-2 mt-1">
                        <button type="button" onClick={() => setPaymentData({...paymentData, payment_method: 'cash'})}
                          className={`flex-1 py-2 rounded-md border text-sm font-medium transition-colors ${paymentData.payment_method === 'cash' ? 'bg-accent text-accent-foreground border-accent' : 'border-input bg-background'}`}>
                          Cash
                        </button>
                        <button type="button" onClick={() => setPaymentData({...paymentData, payment_method: 'bank'})}
                          className={`flex-1 py-2 rounded-md border text-sm font-medium transition-colors ${paymentData.payment_method === 'bank' ? 'bg-accent text-accent-foreground border-accent' : 'border-input bg-background'}`}>
                          Bank Transfer
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('notes')}</Label>
                      <Input value={paymentData.notes} onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})} data-testid="payment-notes-input" />
                    </div>
                    <Button type="submit" className="w-full bg-accent hover:bg-accent/90" data-testid="payment-submit-btn">
                      <span lang={language === 'ar' ? 'ar' : 'en'}>{t('save')}</span>
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>}
            </div>

            <div className="flex justify-end mb-4">
              <select
                value={yearFilter}
                onChange={e => setYearFilter(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="all">All Years</option>
                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>

            <div className="space-y-6">
              {/* Invoices Section */}
              <div>
                <h3 className="text-lg font-semibold mb-3" lang={language === 'ar' ? 'ar' : 'en'}>{t('invoices')}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('invoiceNumber')}</th>
                        <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('date')}</th>
                        <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('type')}</th>
                        <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('amount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInvoices.map((invoice, idx) => (
                        <tr key={idx} className="hover:bg-muted/30" data-testid={`invoice-row-${idx}`}>
                          <td className="border border-border p-3">#{invoice.invoice_number}</td>
                          <td className="border border-border p-3">{formatDate(invoice.date)}</td>
                          <td className="border border-border p-3">{invoice.invoice_type || 'invoice'}</td>
                          <td className="border border-border p-3 text-end font-mono-numeric">{invoice.total.toFixed(2)} {t('currency')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredInvoices.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <p lang={language === 'ar' ? 'ar' : 'en'}>{t('noInvoices')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Payments Section */}
              <div>
                <h3 className="text-lg font-semibold mb-3" lang={language === 'ar' ? 'ar' : 'en'}>{t('payments')}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('date')}</th>
                        <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('amount')}</th>
                        <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('notes')}</th>
                        {user?.role === 'admin' && <th className="border border-border p-3 w-12"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPayments.map((payment, idx) => (
                        <tr key={idx} className="hover:bg-muted/30" data-testid={`payment-row-${idx}`}>
                          <td className="border border-border p-3">{formatDate(payment.date)}</td>
                          <td className="border border-border p-3 text-end font-mono-numeric text-success">+{payment.amount.toFixed(2)} {t('currency')}</td>
                          <td className="border border-border p-3">{payment.notes || '-'}</td>
                          {user?.role === 'admin' && (
                            <td className="border border-border p-3 text-center">
                              <Button size="sm" variant="ghost" onClick={() => handleDeletePayment(payment.id)}>
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredPayments.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <p lang={language === 'ar' ? 'ar' : 'en'}>{t('noPayments')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Running Balance */}
              <div className="bg-muted/30 p-4 rounded-md">
                <h3 className="text-lg font-semibold mb-3" lang={language === 'ar' ? 'ar' : 'en'}>{t('runningBalance')}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('totalInvoiced')}:</span>
                    <span className="font-mono-numeric font-semibold">{totalInvoices.toFixed(2)} {t('currency')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('totalPaid')}:</span>
                    <span className="font-mono-numeric font-semibold text-success">-{totalPayments.toFixed(2)} {t('currency')}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-border">
                    <span className="font-semibold">{t('balance')}:</span>
                    <span className={`text-2xl font-bold font-mono-numeric ${runningBalance > 0 ? 'text-warning' : 'text-success'}`}>
                      {runningBalance.toFixed(2)} {t('currency')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};